// @ts-strict-ignore
import inRange from 'lodash/inRange';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import sumBy from 'lodash/sumBy';
import pick from 'lodash/pick';
import orderBy from 'lodash/orderBy';
import {produce} from 'immer';
import isEmpty from 'lodash/isEmpty';

import {commaizeNumber, formatMetricNumberWithUnits, formatPercent} from 'common/util';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {FlatSortConfig, SortBy, SortByColumnsConfig, SortConfig, SortOrder} from 'common/types/reports/bookmark';
import {PropertyType} from 'common/report/constants';
import {reporter} from 'common/report/error-reporting';
import {OTHER_PROP} from 'common/report/funnels/models/constants';
import {formatConciseDuration} from 'common/util/date';
import {AxisUnitPrecision} from 'common/report/insights/util/types';

import {
  CellRenderContext,
  ColSortOptionValue,
  ColumnGroup,
  Columns,
  ColumnWidthOverrides,
  CommonDataTableFields,
  DataColumn,
  DataFormatType,
  ParentSegmentBlockNode,
  ParentSegmentMetadataNode,
  ParentSegmentNode,
  RowType,
  SegmentBlockNode,
  SegmentCellBlock,
  SegmentColumn,
  SegmentMetadataNode,
  SegmentMetricMetadata,
  SegmentNode,
  TableColumn,
  TableColumnType,
  TableDataType,
  TableRow,
  TreeNodeType,
} from './types';

export function getNumSegments(columns: TableColumn[]) {
  return columns.filter(isSegmentColumn).length;
}

export function invertSortOrder(sortOrder: SortOrder) {
  return sortOrder === SortOrder.Descending ? SortOrder.Ascending : SortOrder.Descending;
}

export function getSortSelectSections({
  columns,
  column,
  sortConfig,
}: {
  columns: TableColumn[];
  column: SegmentColumn;
  sortConfig: SortConfig;
}): Array<Section<ColSortOptionValue>> {
  const labelSortAsc = {sortBy: SortBy.Label, sortOrder: SortOrder.Ascending};
  const labelSortDesc = {sortBy: SortBy.Label, sortOrder: SortOrder.Descending};
  const segmentIndex = parseInt(column.field.slice(CommonDataTableFields.SegmentPrefix.length));
  const colSortConfig = sortConfig.colSortAttrs[segmentIndex];
  const sortingByColumns = sortConfig.sortBy === SortBy.Column;

  const valueItems = getSortSelectValueItems({
    columns,
    colSortConfig,
    segmentIndex,
    sortingByColumns,
  });

  let labelSortTextParts: [string, string];
  if (column.contentType === PropertyType.Number) {
    labelSortTextParts = [`1`, `100`];
  } else if (column.contentType === PropertyType.Datetime) {
    labelSortTextParts = [`Jan`, `Dec`];
  } else {
    labelSortTextParts = [`A`, `Z`];
  }

  return [
    {
      items: [
        {
          icon: `sort-alpha-asc`,
          label: `Segment ${labelSortTextParts[0]} → ${labelSortTextParts[1]}`,
          value: {
            segmentIndex,
            sort: labelSortAsc,
          },
          isSelected: sortingByColumns && isEqual(labelSortAsc, colSortConfig),
        },
        {
          icon: `sort-alpha-desc`,
          label: `Segment ${labelSortTextParts[1]} → ${labelSortTextParts[0]}`,
          value: {
            segmentIndex,
            sort: labelSortDesc,
          } as ColSortOptionValue,
          isSelected: sortingByColumns && isEqual(labelSortDesc, colSortConfig),
        },
        ...valueItems,
      ],
    },
  ];
}

function getColSortOptionValue({
  segmentIndex,
  sortOrder,
  valueField,
}: {
  segmentIndex: number;
  sortOrder: SortOrder;
  valueField: string;
}): ColSortOptionValue {
  return {
    segmentIndex,
    sort: {sortBy: SortBy.Value, sortOrder, valueField},
  };
}

function getValueSortSelectSections({
  colSortConfig,
  segmentIndex,
  sortOrder,
  parentSelected,
  columns,
}: {
  colSortConfig: FlatSortConfig;
  segmentIndex: number;
  sortOrder: SortOrder;
  parentSelected: boolean;
  columns: TableColumn[];
}): Section[] {
  const sectionItems = columns.reduce((accum, col) => {
    if (col.sortSelectTitle && isDataColumn(col)) {
      const icon = {
        [TableDataType.Text]: `type-text`,
        [TableDataType.Number]: `type-number`,
      }[col.contentType];
      const value = getColSortOptionValue({segmentIndex, sortOrder, valueField: col.field});
      accum.push({
        icon,
        label: col.sortSelectTitle,
        value,
        isSelected: parentSelected && colSortConfig.sortBy === SortBy.Value && colSortConfig?.valueField === col.field,
      });
    }
    return accum;
  }, []);
  return [{items: sectionItems}];
}

export function getSortSelectValueItems({
  colSortConfig,
  segmentIndex,
  sortingByColumns,
  columns,
}: {
  colSortConfig: FlatSortConfig;
  segmentIndex: number;
  sortingByColumns: boolean;
  columns: TableColumn[];
}): SectionItem[] {
  let valueItems: SectionItem[];

  const groupSortableCols = columns.filter((col) => col.sortSelectTitle);
  const singleSortableCol = groupSortableCols.length === 1 ? groupSortableCols[0] : null;

  let valueSelected = false;
  if (sortingByColumns && colSortConfig?.sortBy === SortBy.Value) {
    if (singleSortableCol) {
      // the menu item might not be selected if sorting on a "hidden" column, like cohortSize
      valueSelected = colSortConfig?.valueField === singleSortableCol.field;
    } else {
      valueSelected = true;
    }
  }

  const ascValueSelected = valueSelected && colSortConfig?.sortOrder === SortOrder.Ascending;
  const descValueSelected = valueSelected && colSortConfig?.sortOrder === SortOrder.Descending;

  if (singleSortableCol) {
    const valueField = singleSortableCol.field;
    const label = singleSortableCol.sortSelectTitle;

    valueItems = [
      {
        icon: `sort-value-asc`,
        label: `${label} 1 → 100`,
        isSelected: ascValueSelected,
        value: getColSortOptionValue({
          segmentIndex,
          sortOrder: SortOrder.Ascending,
          valueField,
        }),
      },
      {
        icon: `sort-value-desc`,
        label: `${label} 100 → 1`,
        isSelected: descValueSelected,
        value: getColSortOptionValue({
          segmentIndex,
          sortOrder: SortOrder.Descending,
          valueField,
        }),
      },
    ];
  } else {
    const ascValueSubsections = getValueSortSelectSections({
      colSortConfig,
      segmentIndex,
      sortOrder: SortOrder.Ascending,
      parentSelected: ascValueSelected,
      columns,
    });
    const descValueSubsections = getValueSortSelectSections({
      colSortConfig,
      segmentIndex,
      sortOrder: SortOrder.Descending,
      parentSelected: descValueSelected,
      columns,
    });

    valueItems = [
      {
        icon: `sort-value-asc`,
        label: `Value 1 → 100`,
        subSections: ascValueSubsections,
        isSelected: ascValueSelected,
      },
      {
        icon: `sort-value-desc`,
        label: `Value 100 → 1`,
        subSections: descValueSubsections,
        isSelected: descValueSelected,
      },
    ];
  }
  return valueItems;
}

export function isSegmentColumn(column: TableColumn): column is SegmentColumn {
  return column.type === TableColumnType.Segment;
}

export function isDataColumn(column: TableColumn): column is DataColumn {
  return column.type === TableColumnType.Data;
}

export function isTextStyleColumn(column: TableColumn) {
  return (
    column.type === TableColumnType.Segment ||
    (column.type === TableColumnType.Data && column.contentType === TableDataType.Text)
  );
}

export function isNumberStyleColumn(column: TableColumn) {
  return column.type === TableColumnType.Data && column.contentType === TableDataType.Number;
}

export function isSameRowByContext(contextA: CellRenderContext, contextB: CellRenderContext) {
  if (!contextA || !contextB) {
    return false;
  } else if (contextA.rowType === RowType.GroupHeader && contextB.rowType === RowType.GroupHeader) {
    return true;
  } else if (contextA.rowType === RowType.Header && contextB.rowType === RowType.Header) {
    return true;
  } else if (contextA.rowType === RowType.Body && contextB.rowType === RowType.Body) {
    return Boolean(contextA.rowIdx === contextB.rowIdx && contextA.groupedRowIdx === contextB.groupedRowIdx);
  }
  return false;
}

export function isSameCellByContext(contextA: CellRenderContext, contextB: CellRenderContext) {
  return isSameRowByContext(contextA, contextB) && contextA.columnIdx === contextB.columnIdx;
}

export function getSummedColumnWidth(columns: TableColumn[]) {
  const lastColumn = last(columns);
  return columns.length ? lastColumn.offsetLeft + lastColumn.computedWidth - columns[0].offsetLeft : 0;
}

/** The width that all passed columns will take up. Same as `getSummedColumnWidth` but does not
 * require col.offsetLeft to be set */
export function getTotalColumnsWidth({columns, gapSize}: {columns: TableColumn[]; gapSize: number}) {
  return sumBy(columns, (col) => col.computedWidth) + gapSize * (columns.length - 1);
}

export function getResizedColIndices(columnWidthOverrides: ColumnWidthOverrides) {
  return Object.keys(columnWidthOverrides).map((colIdx) => parseInt(colIdx));
}

export function updateWidthOverridesOnColumnsChange({
  newColumns,
  oldOverrides,
}: {
  newColumns: TableColumn[];
  oldOverrides: ColumnWidthOverrides;
}): ColumnWidthOverrides | null {
  if (isEmpty(oldOverrides)) {
    return null;
  }

  const newOverrides = {};
  getResizedColIndices(oldOverrides).forEach((colIndex) => {
    const col = newColumns[colIndex];
    if (col && col.resizable && col.sticky) {
      newOverrides[colIndex] = oldOverrides[colIndex];
    }
  });

  return newOverrides;
}

export function areIntersectingRanges(
  [range1StartIdx, range1EndIdx]: [number, number],
  [range2StartIdx, range2EndIdx]: [number, number],
): boolean {
  const range1InclusiveEndIdx = range1EndIdx - 1;
  const range2InclusiveEndIdx = range2EndIdx - 1;
  return (
    inRange(range2StartIdx, range1StartIdx, range1EndIdx) ||
    inRange(range2InclusiveEndIdx, range1StartIdx, range1EndIdx) ||
    inRange(range1StartIdx, range2StartIdx, range2EndIdx) ||
    inRange(range1InclusiveEndIdx, range2StartIdx, range2EndIdx)
  );
}

function getNumKeysFromTableRow({rows, keyPrefix}: {rows: TableRow[]; keyPrefix: string}): number {
  if (!rows.length) {
    return 0;
  }

  let nonOtherIndex = rows.findIndex((x) => x.identifier !== OTHER_PROP);
  nonOtherIndex = nonOtherIndex < 0 ? 0 : nonOtherIndex;

  let numKeys = 0;
  while (Object.prototype.hasOwnProperty.call(rows[nonOtherIndex], `${keyPrefix}${numKeys}`)) {
    numKeys += 1;
  }

  return numKeys;
}

/**
 * The tree format generated by this function more directly to the UI and is easier to reason/work with for computing
 * some of the SegmentCellBlock fields. (Long-term, it might make sense to use this as the input format to data-table
 * when there's segmented sorting).
 */
export function generateSegmentTreeFromRows({
  rows,
  excludeRowFromLeaf,
}: {
  rows: TableRow[];
  // for testing purposes
  excludeRowFromLeaf?: boolean;
}): ParentSegmentNode {
  if (!rows.length) {
    return {type: TreeNodeType.Parent, label: `$root`, children: []};
  }

  const numSegments = getNumKeysFromTableRow({rows, keyPrefix: CommonDataTableFields.SegmentPrefix});
  const segmentKeys = Array.from({length: numSegments}).map((_, segmentIdx) => {
    return `${CommonDataTableFields.SegmentPrefix}${segmentIdx}`;
  });
  const rootSegmentNode: ParentSegmentNode = {type: TreeNodeType.Parent, label: `$root`, children: []};

  rows.forEach((row, rowIdx) => {
    let currSegmentNode: SegmentNode = rootSegmentNode;
    let newAncestorNode = false;
    const prevRow = rowIdx === 0 ? null : rows[rowIdx - 1];
    for (const [segmentIdx, segmentKey] of segmentKeys.entries()) {
      newAncestorNode = newAncestorNode || Boolean(prevRow && prevRow[segmentKey] !== row[segmentKey]);
      if (newAncestorNode || rowIdx === 0) {
        let newSegmentNode: SegmentNode;
        if (segmentIdx === segmentKeys.length - 1) {
          newSegmentNode = {
            type: TreeNodeType.Leaf,
            label: row[segmentKey],
            row: excludeRowFromLeaf ? null : row,
          };
        } else {
          newSegmentNode = {
            type: TreeNodeType.Parent,
            label: row[segmentKey],
            children: [],
          };
        }
        (currSegmentNode as ParentSegmentNode).children.push(newSegmentNode);
        currSegmentNode = newSegmentNode;
      } else if (segmentIdx !== segmentKeys.length - 1) {
        currSegmentNode = last((currSegmentNode as ParentSegmentNode).children);
      }
    }
  });
  return rootSegmentNode;
}

export function hasSegmentMetricMetadata(metadata: SegmentMetricMetadata): boolean {
  return Boolean(
    metadata &&
      (metadata.metricOverall !== undefined ||
        metadata.comparisonOverall !== undefined ||
        metadata.liftOverall !== undefined),
  );
}

function updateSegmentTreeWithCellBlockData({
  node,
  flatRowStartIdx,
  rowStartIdx,
  colOffset,
  segmentIdx,
  numMetrics,
  segmentMetadata,
}: {
  node: SegmentBlockNode;
  flatRowStartIdx: number;
  rowStartIdx: number;
  colOffset: number;
  segmentIdx: number;
  numMetrics: number;
  segmentMetadata?: SegmentMetadataNode;
}): void {
  node.flatRowStartIdx = flatRowStartIdx;
  node.rowStartIdx = rowStartIdx;
  node.columnIdx = colOffset + segmentIdx;

  if (numMetrics > 0 && segmentMetadata) {
    node.metricMetadatas = segmentMetadata.metricMetadatas;
  }

  if (node.type === TreeNodeType.Parent) {
    const segmentMetadataMap = segmentMetadata
      ? new Map(
          (segmentMetadata as ParentSegmentMetadataNode).children.map((childNode) => [childNode.label, childNode]),
        )
      : null;
    node.children.forEach((childNode, childNodeIdx) => {
      const prevChildNode = childNodeIdx === 0 ? null : node.children[childNodeIdx - 1];
      updateSegmentTreeWithCellBlockData({
        node: childNode,
        flatRowStartIdx: prevChildNode ? prevChildNode.flatRowEndIdx : flatRowStartIdx,
        rowStartIdx: prevChildNode ? prevChildNode.rowEndIdx : rowStartIdx,
        colOffset,
        segmentIdx: segmentIdx + 1,
        numMetrics,
        segmentMetadata: segmentMetadataMap?.get(childNode.label),
      });
    });
    node.flatRowEndIdx = last(node.children).flatRowEndIdx;
    node.rowEndIdx = last(node.children).rowEndIdx;
  } else {
    const nodeRow = node.row;
    const hasExpandedGroupedRows = !nodeRow.isCollapsed && Boolean(nodeRow.groupedRows?.length);
    const rootAndGroupedRowsCount = 1 + (hasExpandedGroupedRows ? nodeRow.groupedRows.length : 0);
    node.flatRowEndIdx = flatRowStartIdx + rootAndGroupedRowsCount;
    node.rowEndIdx = rowStartIdx + 1;
  }
}

/**
 * Updates every parent node start and end index by x2 when there are comparison rows. We do this
 * because in funnel steps chart, when there is time comparison, an extra row gets added for each
 * segment. Thus, the parent nodes start and end index should be doubled.
 */
function updateComparisonSegmentTree(node: SegmentBlockNode): void {
  if (node.type === TreeNodeType.Parent) {
    node.flatRowStartIdx *= 2;
    node.flatRowEndIdx *= 2;
    node.rowStartIdx *= 2;
    node.rowEndIdx *= 2;

    node.children.forEach((childNode) => {
      updateComparisonSegmentTree(childNode);
    });
  }
}

function buildSegmentCellBlocksFromTree({
  node,
  segmentIdx,
  segmentCellBlocksOutput,
}: {
  node: SegmentBlockNode;
  segmentIdx: number;
  segmentCellBlocksOutput: SegmentCellBlock[];
}): void {
  if (node.type === TreeNodeType.Leaf) {
    return;
  }

  if (segmentIdx >= 0) {
    const segmentCellBlock = pick(node, [
      `flatRowStartIdx`,
      `flatRowEndIdx`,
      `columnIdx`,
      `rowStartIdx`,
      `rowEndIdx`,
      `label`,
      `metricMetadatas`,
    ]) as SegmentCellBlock;
    segmentCellBlocksOutput.push(segmentCellBlock);
  }
  node.children.forEach((childNode) => {
    buildSegmentCellBlocksFromTree({
      node: childNode,
      segmentIdx: segmentIdx + 1,
      segmentCellBlocksOutput,
    });
  });
}

/**
 * Generates segment label overlays that are displayed for multiseg data in grouped sorting mode.
 */
export function generateSegmentCellBlocks({
  rows,
  colOffset,
  isMultimetricMode,
  segmentMetadata,
  hasComparison = false,
}: {
  rows: TableRow[];
  colOffset: number;
  isMultimetricMode: boolean;
  segmentMetadata?: SegmentMetadataNode;
  hasComparison?: boolean;
}): SegmentCellBlock[] {
  const rootSegmentNode = generateSegmentTreeFromRows({rows});
  updateSegmentTreeWithCellBlockData({
    node: rootSegmentNode,
    flatRowStartIdx: 0,
    rowStartIdx: 0,
    colOffset,
    segmentIdx: -1,
    numMetrics: isMultimetricMode ? getNumKeysFromTableRow({rows, keyPrefix: CommonDataTableFields.MetricPrefix}) : 0,
    segmentMetadata,
  });
  if (hasComparison) {
    updateComparisonSegmentTree(rootSegmentNode);
  }
  const segmentCellBlocksOutput: SegmentCellBlock[] = [];
  buildSegmentCellBlocksFromTree({
    node: rootSegmentNode,
    segmentIdx: -1,
    segmentCellBlocksOutput,
  });
  return orderBy(segmentCellBlocksOutput, [(s) => s.columnIdx, (s) => s.flatRowStartIdx]);
}

export function updateRowsWithSegmentBoundaries({
  rows,
  segmentCellBlocks,
}: {
  rows: TableRow[];
  segmentCellBlocks: SegmentCellBlock[];
}): void {
  segmentCellBlocks.forEach((segmentCellBlock) => {
    rows[segmentCellBlock.rowStartIdx].segmentBlockStarts ||= new Set();
    rows[segmentCellBlock.rowStartIdx].segmentBlockStarts.add(segmentCellBlock.columnIdx);
    rows[segmentCellBlock.rowEndIdx - 1].segmentBlockEnds ||= new Set();
    rows[segmentCellBlock.rowEndIdx - 1].segmentBlockEnds.add(segmentCellBlock.columnIdx);
  });
}

/** Check if segment block labels should be generated, i.e. there are multiple segments in grouped sort mode */
export function segmentCellBlocksEnabled({columns, sorting}: {columns: TableColumn[]; sorting: SortConfig}) {
  return getNumSegments(columns) > 1 && sorting.sortBy === SortBy.Column;
}

export function flattenColumns(columns: Columns): TableColumn[] {
  return columns
    .map((column: TableColumn | ColumnGroup) => {
      if (`columns` in column) {
        return column.columns;
      }
      return column;
    })
    .flat();
}

export function getUnlimitedViewNSortConfig(sortConfig: SortConfig): SortConfig {
  sortConfig = produce(sortConfig, (sortConfigDraft) => {
    sortConfigDraft.colSortAttrs.forEach((colSortAttr) => {
      colSortAttr.viewNLimit = null;
    });
  });
  if (sortConfig.sortBy === SortBy.Value) {
    sortConfig = produce(sortConfig, (sortConfigDraft) => {
      sortConfigDraft.viewNLimit = null;
    });
  }
  return sortConfig;
}

/**
 * Checks if the SortConfig contains View N info. This is determined by the presence/absence of the `viewNLimit` field.
 * Note this differs from having `viewNLimit` of null, which is used unlimited. This distinction is important because
 * SortConfig having View N info (even if unlimited) is used by mp-data-table to determine whether or not to show
 * the View N control.
 */
export function isPostViewNSortConfig(sortConfig: SortConfig): boolean {
  return (
    (sortConfig.sortBy !== SortBy.Value || sortConfig.viewNLimit !== undefined) &&
    sortConfig.colSortAttrs.every((colSortAttr) => colSortAttr.viewNLimit !== undefined)
  );
}

function _filterSegmentsWithViewNLimit({
  node,
  sortConfig,
  segmentIdx,
}: {
  node: SegmentBlockNode;
  sortConfig: SortByColumnsConfig;
  segmentIdx: number;
}): SegmentBlockNode {
  if (node.type === TreeNodeType.Parent) {
    const {viewNLimit} = sortConfig.colSortAttrs[segmentIdx];
    const childNodes = Number.isFinite(viewNLimit) ? node.children.slice(0, viewNLimit) : node.children;
    return {
      ...node,
      children: childNodes.map((childNode) => {
        return _filterSegmentsWithViewNLimit({node: childNode, sortConfig, segmentIdx: segmentIdx + 1});
      }),
    };
  }
  return node;
}

function limitSegmentsWithViewN({
  node,
  sortConfig,
}: {
  node: ParentSegmentBlockNode;
  sortConfig: SortConfig;
}): ParentSegmentBlockNode {
  if (sortConfig.sortBy !== SortBy.Column) {
    reporter.warn(`limitSegmentsWithViewN should only be used for SortBy.Column`, {sortConfig});
    return node;
  }
  return _filterSegmentsWithViewNLimit({node, sortConfig, segmentIdx: 0}) as ParentSegmentBlockNode;
}

function _flattenSegmentTreeToRows(node: SegmentNode, outputRows: TableRow[]) {
  if (node.type === TreeNodeType.Parent) {
    node.children.forEach((childNode) => {
      _flattenSegmentTreeToRows(childNode, outputRows);
    });
  } else {
    outputRows.push(node.row);
  }
}

function flattenSegmentTreeToRows(node: ParentSegmentNode): TableRow[] {
  const outputRows: TableRow[] = [];
  _flattenSegmentTreeToRows(node, outputRows);
  return outputRows;
}

export function filterSortedRowsWithViewN({
  rows,
  columns,
  sortConfig,
}: {
  rows: TableRow[];
  columns: Columns;
  sortConfig: SortConfig;
}): TableRow[] {
  const numSegments = getNumSegments(flattenColumns(columns));
  if (numSegments === 0) {
    // Special case for metric without breakdown
    return rows;
  }

  if (sortConfig.sortBy === SortBy.Value) {
    return Number.isFinite(sortConfig.viewNLimit) ? rows.slice(0, sortConfig.viewNLimit) : rows;
  }
  const segmentRoot = generateSegmentTreeFromRows({rows});
  const filteredSegmentRoot = limitSegmentsWithViewN({node: segmentRoot, sortConfig});
  return flattenSegmentTreeToRows(filteredSegmentRoot);
}

export function canUseViewN(): boolean {
  return true;
}

export function getViewNLimit({sortConfig, segmentIdx}: {sortConfig: SortConfig; segmentIdx: number}): number {
  const isColumnSort = sortConfig.sortBy === SortBy.Column;
  return isColumnSort ? sortConfig.colSortAttrs[segmentIdx].viewNLimit : sortConfig.viewNLimit;
}

/** When formatAsDecimal is true we need to format the number such that it's always as a decimal
   with two digits after the decimal point.*/
export function commaizeAndFormatAsDecimal(number: number, formatAsDecimal: boolean) {
  return formatAsDecimal ? commaizeNumber(number, {maxPrecision: 2, exactPrecision: true}) : commaizeNumber(number);
}

/** Always render percentages as fixed point decimals in the table for consistency.*/
export function formatPercentForDataTable(percent: number) {
  return formatPercent(percent, {precision: 2, exactPrecision: true});
}

export function getFormattedValue(
  value: number,
  dataFormatType: DataFormatType,
  options: {abbrev: boolean; precision: AxisUnitPrecision | null} = {
    abbrev: true,
    precision: null,
  },
) {
  // When we force decimal we will just instead default to precision 2. This way
  // if there is a metric or property level defined precision we don't override it.
  if (dataFormatType === DataFormatType.Decimal && options.precision === null) {
    options.precision = 2;
  }

  switch (dataFormatType) {
    case DataFormatType.Time:
      return formatConciseDuration(value);
    case DataFormatType.Percent:
      return formatPercentForDataTable(value);
    case DataFormatType.Decimal:
    default:
      return formatMetricNumberWithUnits(value, options);
  }
}
